import React, { useEffect, useState } from 'react';
import ActivityCard from './ActivityCard';
import { Users, Globe, Zap, Heart, Shield, Award } from 'lucide-react';
import { db } from '../firebase';
import { collection, getDocs, query, limit as firestoreLimit, orderBy } from 'firebase/firestore';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';

interface ActivitiesGridProps {
  limit?: number;
  detailed?: boolean;
}

const ActivitiesGrid: React.FC<ActivitiesGridProps> = ({ limit, detailed = false }) => {
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState<any>(null);

  useEffect(() => {
    const fetchActivities = async () => {
      if (!db) {
        setLoading(false);
        return;
      }
      try {
        let q = query(collection(db, 'activities'), orderBy('createdAt', 'desc'));
        if (limit) {
          q = query(q, firestoreLimit(limit));
        }
        const querySnapshot = await getDocs(q);
        const fetchedActivities = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setActivities(fetchedActivities);
      } catch (err) {
        console.error("Error fetching activities:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchActivities();
  }, [limit]);

  const defaultActivities = [
    {
      id: '1',
      title: 'Equoterapia',
      shortDescription: 'Atividades terapêuticas com cavalos para desenvolvimento motor e emocional.',
      imageUrl: 'https://i.postimg.cc/W1PJ2YcW/Whats-App-Image-2026-03-12-at-09-13-56.jpg',
      detailedContent: `
        <h4 className="text-2xl font-bold text-slate-900 mb-4">Equoterapia na UNIFIG</h4>
        <p className="text-slate-600 mb-4">
          A equoterapia é uma atividade terapêutica que utiliza o cavalo como co-terapeuta, promovendo benefícios físicos, emocionais e sociais para pessoas com deficiência.
        </p>
        <p className="text-slate-600 mb-4">
          Na UNIFIG, oferecemos sessões de equoterapia adaptadas às necessidades individuais de cada participante, com profissionais especializados em equitação terapêutica.
        </p>
        <ul className="list-disc list-inside text-slate-600 space-y-2">
          <li>Melhora da coordenação motora</li>
          <li>Desenvolvimento da autoestima</li>
          <li>Estímulo da socialização</li>
          <li>Redução do estresse e ansiedade</li>
          <li>Fortalecimento muscular e equilíbrio</li>
        </ul>
      `
    },
    {
      id: '2',
      title: 'Pilates',
      shortDescription: 'Exercícios de pilates adaptados para melhorar força e flexibilidade.',
      imageUrl: 'https://i.postimg.cc/9FCfNPN7/Whats-App-Image-2026-04-06-at-09-38-25.jpg',
      detailedContent: `
        <h4 className="text-2xl font-bold text-slate-900 mb-4">Pilates Adaptado na UNIFIG</h4>
        <p className="text-slate-600 mb-4">
        O Pilates para reabilitação do corpo é uma prática terapêutica focada no fortalecimento muscular profundo, reequilíbrio postural e alívio de dores crônicas ou lesões. Utilizando aparelhos e exercícios de solo, promove mobilidade e controle motor, sendo seguro para diversas patologias ao focar na estabilização do powerhouse (centro de força).
        </p>
      `
    },
    {
      id: '3',
      title: 'Atividades Recreativas',
      shortDescription: 'Diversas atividades recreativas para socialização e diversão.',
      imageUrl: 'https://i.postimg.cc/Kv8H0fHP/Whats-App-Image-2026-03-11-at-16-07-14.jpg',
      detailedContent: `
        <h4 className="text-2xl font-bold text-slate-900 mb-4">Atividades Recreativas na UNIFIG</h4>
        <p className="text-slate-600 mb-4">
          As atividades recreativas são essenciais para o desenvolvimento social e emocional, proporcionando momentos de diversão e interação entre os participantes.
        </p>
        <p className="text-slate-600 mb-4">
          Oferecemos uma variedade de atividades adaptadas, sempre com o objetivo de promover a inclusão e o bem-estar.
        </p>
      `
    },
    
    {
      id: '4',
      title: 'Assistência Social',
      shortDescription: 'O assistente social é o profissional que defende direitos sociais e promove a cidadania de indivíduos, famílias e grupos em situação de vulnerabilidade',
      imageUrl: 'https://i.postimg.cc/PJsVX58s/Whats-App-Image-2026-03-26-at-07-55-41.jpg',
      imageUrl: 'https://i.postimg.cc/MKztd40k/Whats-App-Image-2026-03-26-at-07-55-41-(1).jpg',
      detailedContent: `
        <h4 className="text-2xl font-bold text-slate-900 mb-4">Equoterapia na UNIFIG</h4>
        <p className="text-slate-600 mb-4">
          A UNIFIG tem três assistentes sociais que fazem a diferença para instituição e para o município com um trabalho incrível, os atendimentos ocorrem tanto na instituição quanto na casa dos usuários.
        </p>
        <p className="text-slate-600 mb-4">
          A Assistente Social atua na garantia de direitos, viabilizando o acesso a serviços socioassistenciais a saúde para populações em vulnerabilidade. Acolhimento e atendimento direto a usuários, famílias e grupos, identificando necessidades e realizando encaminhamentos, atendimentos individuais e visitas a domicílios.
        </p>
      `
    },
    
    {
      id: '5',
      title: 'CAEE',
      shortDescription: 'Centro de Atendimento Educacional Especializado',
      imageUrl: 'https://i.postimg.cc/vZ4V8LLR/Whats-App-Image-2026-03-31-at-07-27-46.jpg',
      detailedContent: `
        <h4 className="text-2xl font-bold text-slate-900 mb-4">Equoterapia na UNIFIG</h4>
        <p className="text-slate-600 mb-4">
        A UNIFIG juntamente com a Secretaria de Saúde e Educação de Iguatemi oferece atendimentos as crianças da rede municipal de educação, disponibilizando atendimentos com Psicólogas (Clinicas e Escolar), Psicopedagoga, Fonoaudiólogas, além de possibilitar atendimentos aos professores da rede com Psicóloga (Psicanalista) e uma Terapeuta Integrativa.
        </p>

        <p className="text-slate-600 mb-4">
        Como ter o atendimento no CAEE?
        </p>
         <ul className="list-disc list-inside text-slate-600 space-y-2">
          <li>Serão atendidas crianças na idade de 04 anos à 11 anos e 11 meses de idade.</li>
          <li>Os responsáveis legais pela criança deverão levar o encaminhamento até o local indicado (CAEE), localizado na antiga creche São José, no horário comercial de segunda à sexta-feira das 07:00 às 11:00 e 13:00 às 17:00.</li>
        </ul>
        
        <p className="text-slate-600 mb-4">
        Os pacientes serão chamados conforme havendo disponibilidades de vagas.
        Vale ressaltar que a prioridade de atendimento devido a grande demanda será para as crianças da rede municipal de ensino de Iguatemi.
        </p>
      `
    },
    {
      id: '6',
      title: 'Natação',
      shortDescription: 'Confira os benefícios da natação para os nossos pacientes!',
      imageUrl: 'https://i.postimg.cc/L61VNpwH/Whats-App-Image-2026-04-06-at-09-38-24.jpg',
      detailedContent: `
        <h4 className="text-2xl font-bold text-slate-900 mb-4">NATAÇÃO na UNIFIG</h4>
        <p className="text-slate-600 mb-4">
        A natação é uma das atividades físicas mais recomendadas para autistas, oferecendo benefícios terapêuticos, motores e sociais. 
        </p>
        <p className="text-slate-600 mb-4">
        Benefícios da Natação:
        </p>
        <ul className="list-disc list-inside text-slate-600 space-y-2">
          <li>Promove o relaxamento;</li>
          <li>Melhora a coordenação motora;</li>
          <li>Equilíbrio e força muscular;</li>
          <li>Reduz a ansiedade e comportamentos repetitivos;</li>
          <li>Também é crucial para aumentar a autoconfiança e a segurança…</li>
        </ul>
      `
    },
    
     {
      id: '7',
      title: 'Psicoterapia',
      shortDescription: 'Sou Evelyn Susan, psicologa a 13 anos, e venho atuando na Unifig a 5 anos.',
      imageUrl: 'https://i.postimg.cc/T3C9GKJ5/Whats-App-Image-2026-04-16-at-10-05-24.jpg',
      detailedContent: `
        <h4 className="text-2xl font-bold text-slate-900 mb-4">Equoterapia na UNIFIG</h4>
        <p className="text-slate-600 mb-4">
O trabalho do psicólogo com pacientes neurológicos foca na avaliação, reabilitação cognitiva e suporte emocional, visando adaptar o paciente às novas limitações e minimizar o impacto psíquico de lesões cerebrais ou doenças crônicas. Atua de forma interdisciplinar (com neurologistas e terapeutas) para melhorar a qualidade de vida e funcionalidade. 
        </p>
        <p className="text-slate-600 mb-4">
O acompanhamento também visa em realizar palestras educativas e visitas domiciliares quando necessário. A auriculoterapia visa em anemizar e estimular pontos específicos no pavilhão auricular (orelha), para tratar dores, distúrbios físicos, mentais e emocionais.</p>
      `
    },
    {
      id: '8',
      title: 'Psicomotricidade',
      shortDescription: 'A psicomotricidade é uma área que integra corpo e mente.',
      imageUrl: 'https://i.postimg.cc/FsynWjdV/Whats-App-Image-2026-05-04-at-12-42-16-(1).jpg',
      detailedContent: `
        <h4 className="text-2xl font-bold text-slate-900 mb-4">SOBRE A Psicomotricidade:</h4>
        <p className="text-slate-600 mb-4">
        considerando que o movimento não é apenas físico, mas também está ligado às emoções, ao pensamento e à forma como a criança se relaciona com o mundo. No desenvolvimento infantil, ela exerce um papel fundamental, pois contribui para a construção da coordenação motora, do equilíbrio, da noção de espaço, do ritmo e da organização corporal.
Em crianças com Transtorno do Espectro Autista (TEA), a psicomotricidade ganha ainda mais relevância. Muitas dessas crianças apresentam dificuldades na coordenação motora, no planejamento dos movimentos, na percepção corporal e na interação com o ambiente. Nesse contexto, as atividades psicomotoras ajudam a desenvolver habilidades básicas, como correr, pular, equilibrar-se e manipular objetos, além de favorecer a autonomia e a segurança nas ações do dia a dia.
Outro ponto importante é que a psicomotricidade também auxilia no desenvolvimento social e emocional. Por meio de brincadeiras dirigidas e atividades lúdicas, a criança com autismo pode melhorar sua capacidade de interação, comunicação e expressão de sentimentos. O uso do corpo como meio de expressão contribui para reduzir tensões, aumentar a atenção e estimular o vínculo com o outro.
Além disso, as práticas psicomotoras promovem a organização do comportamento, ajudando a criança a compreender regras, limites e sequências de ações. Isso impacta diretamente no aprendizado escolar e na adaptação a diferentes ambientes.
Portanto, a psicomotricidade é uma ferramenta essencial no desenvolvimento motor de crianças com autismo, pois vai além do movimento, promovendo avanços cognitivos, emocionais e sociais. Quando trabalhada de forma planejada e respeitando as individualidades de cada criança, ela contribui significativamente para uma melhor qualidade de vida e inclusão.
        </p>
      `
    },

    {
      id: '9',
      title: 'Psicopedagogia',
      shortDescription: 'Psicopedagogia.',
      imageUrl: 'https://i.postimg.cc/52LYZ6j9/Whats-App-Image-2026-05-29-at-14-53-32.jpg',
      detailedContent: `
        <h4 className="text-2xl font-bold text-slate-900 mb-4">A psicopedagogia:</h4>
        <p className="text-slate-600 mb-4">
        A psicopedagogia é uma área interdisciplinar que estuda e identifica as dificuldades associadas a aprendizagem em crianças, adolescentes e adultos. Essa área busca desenvolver métodos e estratégias que facilitem a aprendizagem, atuando tanto na prevenção quanto na solução de problemas.
        O objetivo principal e a de ajudar a superar barreiras de defasagem no aprendizado e contribuir para o desenvolvimento integral dos indivíduos. O Psicopedagogo realiza a intervenção e orientação para atender às necessidades de cada pessoa.
        </p>
      `
    },

    {
      id: '10',
      title: 'Terapia Fonoaudiológica',
      shortDescription: 'Atuação Fonoaudiológica na UNIFIG.',
      imageUrl: 'https://i.postimg.cc/65GyvyVs/Whats-App-Image-2026-06-03-at-09-37-10.jpg',
      detailedContent: `
        <h4 className="text-2xl font-bold text-slate-900 mb-4">UNIFIG:</h4>
        <p className="text-slate-600 mb-4">
        Atuação Fonoaudiológica na UNIFIG consiste em avaliação e acompanhamento terapêutico de pessoas com transtornos do neurodesenvolvimento e com alterações na comunicação decorrentes de condições neurológicas, genéticas ou adquiridas, como o AVC. O trabalho inclui avaliações fonoaudiológicas individualizadas e fonoterapias voltadas ao desenvolvimento da linguagem, fala e comunicação, com foco em motricidade orofacial, fluência, respiração, atenção e habilidades comunicativas. 
        As intervenções são planejadas de acordo com as necessidades de cada paciente, visando promover autonomia, funcionalidade e qualidade de vida.
        </p>
      `
    },
    
     {
      id: '11',
      title: 'Equoterapia',
      shortDescription: 'Desenvolvimento que transforma vidas.',
      imageUrl: 'https://i.postimg.cc/FKrqrMwx/Whats-App-Image-2026-07-08-at-10-23-13.jpg',
      detailedContent: `
        <h4 className="text-2xl font-bold text-slate-900 mb-4">UNIFIG:</h4>
        <p className="text-slate-600 mb-4">
        A equoterapia é um método terapêutico que utiliza o cavalo como facilitador do desenvolvimento físico, emocional, cognitivo e social. Por meio do movimento tridimensional do animal, são estimulados o equilíbrio, a coordenação motora, a postura, a força muscular, a atenção, a concentração e a autoconfiança.
Cada atendimento é planejado de forma individualizada, respeitando as necessidades e os objetivos de cada praticante. Mais do que promover ganhos motores, a equoterapia fortalece a autonomia, a autoestima e a inclusão, proporcionando novas conquistas a cada sessão.
Seguimos comprometidos em oferecer um atendimento humanizado, seguro e de qualidade, acreditando que cada pequeno avanço representa uma grande vitória.
        </p>
      `
    },

    {
      id: '12',
      title: 'Atendimento Nutricional Infantil',
      shortDescription: 'Intervenção na Seletividade Alimentar em Crianças com TEA.',
      imageUrl: 'https://i.postimg.cc/MGLYCgwf/Whats-App-Image-2026-08-03-at-07-55-42.jpg',
      detailedContent: `
        <h4 className="text-2xl font-bold text-slate-900 mb-4">UNIFIG:</h4>
        <p className="text-slate-600 mb-4">
        Na UNIFIG, o atendimento nutricional infantil vai muito além da alimentação. Desenvolvemos estratégias terapêuticas voltadas para crianças com Transtorno do Espectro Autista (TEA) que apresentam seletividade alimentar, respeitando o tempo, as necessidades e as particularidades de cada criança.
Durante as sessões, são realizadas atividades lúdicas e sensoriais que estimulam o contato gradual com os alimentos por meio da exploração de texturas, aromas, cores, formatos e diferentes consistências. O objetivo inicial não é fazer a criança comer, mas permitir que ela conheça o alimento, toque, cheire, manipule, brinque e se familiarize com ele, reduzindo a ansiedade e favorecendo uma relação mais positiva com a alimentação.
As intervenções incluem brincadeiras com frutas, legumes e outros alimentos, atividades de pintura, associação de figuras, exploração tátil, identificação de cheiros e exercícios de coordenação motora, sempre adaptados ao perfil sensorial de cada paciente.
Esse processo contribui para:
•⁠  ⁠Ampliar a aceitação de novos alimentos;
•⁠  ⁠Reduzir a resistência durante as refeições;
•⁠  ⁠Estimular a autonomia alimentar;
•⁠  ⁠Desenvolver habilidades sensoriais e motoras;
•⁠  ⁠Promover uma relação saudável e prazerosa com a comida.
A participação da família é essencial durante todo o tratamento, permitindo que as estratégias sejam reforçadas também em casa e favorecendo resultados mais consistentes.
Na UNIFIG, acreditamos que cada pequena conquista representa um grande avanço. Com acolhimento, conhecimento técnico e intervenções baseadas em evidências, buscamos proporcionar uma experiência alimentar mais tranquila, respeitosa e significativa para cada criança.
Nutricionista:
Kathyellen Vargas de Oliveira de Freitas – CRN3 56257
        </p>
      `
    }
  
  ];

  const displayActivities = activities.length > 0 ? activities : defaultActivities;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {displayActivities.map((activity, index) => (
          <ActivityCard
            key={activity.id || index}
            title={activity.title}
            description={activity.shortDescription}
            icon={Users} // Default icon or map from data if needed
            image={activity.imageUrl}
            detailed={detailed}
            onLearnMore={() => setSelectedActivity(activity)}
          />
        ))}
      </div>

      {/* Activity Modal */}
      <AnimatePresence>
        {selectedActivity && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedActivity(null)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative bg-white w-full max-w-4xl rounded-[3rem] shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
            >
              <div className="p-10">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-3xl font-black text-slate-900">
                    {selectedActivity.title}
                  </h3>
                  <button type="button" onClick={() => setSelectedActivity(null)} className="text-slate-400 hover:text-slate-600">
                    <X size={24} />
                  </button>
                </div>

                {selectedActivity.imageUrl && (
                  <div className="mb-6 rounded-2xl overflow-hidden">
                    <img
                      src={selectedActivity.imageUrl}
                      alt={selectedActivity.title}
                      className="w-full h-auto"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                )}

                <div
                  className="prose prose-slate max-w-none"
                  dangerouslySetInnerHTML={{ __html: selectedActivity.detailedContent }}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ActivitiesGrid;
